"use client";

import React from "react";
import { Network, Users, ChartBar, Target, Layers, Lightbulb, Compass, Workflow } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const tools = [{
  icon: Network,
  title: "Ecosystem Mapping",
  definition: "Visualizing the complex web of internal and external learning touchpoints.",
  benefit: "Reveals hidden silos and identifies high-impact opportunities for cross-functional integration."
}, {
  icon: Users,
  title: "Learner Journey Analytics",
  definition: "Data-driven tracking of the student experience from inquiry to alumni status.",
  benefit: "Pinpoints friction points in the lifecycle to improve retention and lifelong engagement."
}, {
  icon: Layers,
  title: "Capability Maturity Model",
  definition: "A structured framework to assess current organizational readiness for growth.",
  benefit: "Provides a clear, objective baseline to measure progress and prioritize investment."
}, {
  icon: Workflow,
  title: "Co-Design Workshops",
  definition: "Collaborative sessions that bring faculty, staff, and leadership together.",
  benefit: "Builds internal ownership of the strategy, ensuring smoother implementation and adoption."
}, {
  icon: ChartBar,
  title: "Program Viability Audit",
  definition: " rigorous financial and market analysis of existing academic portfolios.",
  benefit: "Optimizes resource allocation by identifying which programs to grow, sustain, or sunset."
}, {
  icon: Target,
  title: "Strategic Alignment Matrix",
  definition: "Ensuring every initiative maps directly to the institution's core mission.",
  benefit: "Prevents mission drift and ensures that growth efforts reinforce institutional identity."
}, {
  icon: Lightbulb,
  title: "Innovation Labs",
  definition: "Rapid prototyping environments for testing new learning models safely.",
  benefit: "Reduces risk by validating new ideas on a small scale before institution-wide rollout."
}, {
  icon: Compass,
  title: "Governance Frameworks",
  definition: "Designing decision-making structures that support agile, distributed leadership.",
  benefit: "Empowers teams to act quickly while maintaining institutional coherence and accountability."
}];
export default function ToolsMethodologies() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="tools-methodologies" className="py-16 bg-background relative overflow-hidden" ref={ref}>
      {}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-50" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-50" />
      
      <div className="container mx-auto px-6 md:px-12">
        <div className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-primary mb-6">
            Tools & Methodologies
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">We don't just deliver reports; we equip your team with a robust toolkit designed for you, and with you. These methodologies are built to be adopted, adapted, and owned by your institution.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {tools.map((tool, i) => <div key={i} data-index={i} className={`transition-all duration-700 delay-[${i * 100}ms] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="h-full cursor-help">
                    <Card className="h-full border border-border/60 bg-card/50 hover:bg-card hover:border-secondary/30 hover:shadow-md transition-all duration-300 group">
                      <CardHeader className="pb-3 space-y-4">
                        <div className="size-10 rounded-lg bg-primary/5 flex items-center justify-center text-primary group-hover:bg-secondary/10 group-hover:text-secondary transition-colors duration-300">
                          <tool.icon className="size-5" />
                        </div>
                        <CardTitle className="text-lg font-medium text-foreground group-hover:text-primary transition-colors">
                          {tool.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                          {tool.definition}
                        </CardDescription>
                      </CardContent>
                    </Card>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs bg-primary text-primary-foreground p-4 border-none shadow-xl">
                  <p className="font-semibold mb-1 text-xs uppercase tracking-wider text-secondary-foreground/80">How it helps</p>
                  <p className="text-sm leading-snug">{tool.benefit}</p>
                </TooltipContent>
              </Tooltip>
            </div>)}
        </div>
      </div>
    </section>;
}